import {
  GroupIcon,
  CoursesIcon,
  DasboardIcon,
  GradesIcon,
  ReportsIcon,
  SettingsIcon,
  StudyIcon,
  SpacesIcon,
} from '@/assets/nav';
import type { EffectiveRole, RolePermissionFlags, role } from 'auth-types';
import EffectiveRoleService from '@/services/auth/effective-role-service';
import UsersRolesIcon from '@/assets/nav/users-roles.svg';

// Define the types for navigation items
export interface NavItem {
  name: string;
  route: string;
  subItems?: SubNavItem[];
  icon?: any;
  requiredPermission?: keyof RolePermissionFlags;
}

export interface SubNavItem {
  name: string;
  route: string;
  requiredPermission?: keyof RolePermissionFlags;
}

type HiddenAccessRoute = {
  route: string;
  requiredPermission?: keyof RolePermissionFlags;
};

type NavRoleKey = 'student' | 'admin';

// Adjust NavItemMap to use the NavItem type
export const NavItemMap: Record<NavRoleKey, Array<NavItem>> = {
  student: [
    { name: 'Dashboard', route: '/student', icon: DasboardIcon },
    {
      name: 'Course',
      route: '/student/course',
      icon: CoursesIcon,
      requiredPermission: 'perm_course_view',
    },
    {
      name: 'Study',
      route: '/student/study',
      icon: StudyIcon,
      requiredPermission: 'perm_ai_tools',
    },
    { name: 'Lesson', route: '/student/lesson' },
    {
      name: 'Flash Cards',
      route: '/student/study/flashcards',
      requiredPermission: 'perm_ai_tools',
    },
    {
      name: 'Flash Cards Review',
      route: '/student/study/flashcards/review',
      requiredPermission: 'perm_ai_tools',
    },
    { name: 'Self Quiz', route: '/student/study/self-quiz', requiredPermission: 'perm_ai_tools' },
    {
      name: 'Self Quiz Review',
      route: '/student/study/self-quiz/review',
      requiredPermission: 'perm_ai_tools',
    },
    { name: 'Tutor Me', route: '/student/study/tutor-me', requiredPermission: 'perm_ai_tools' },
    {
      name: 'Spaces',
      route: '/student/spaces',
      icon: SpacesIcon,
      requiredPermission: 'perm_ai_tools',
    },
  ],
  admin: [
    { name: 'Home', route: '/admin', icon: DasboardIcon },
    {
      name: 'Users and Roles',
      route: '/admin/',
      icon: UsersRolesIcon,
      requiredPermission: 'perm_subdomain_user_manage_students',
      subItems: [
        {
          name: 'Directory',
          route: '/admin/users-and-roles/student-directory',
          requiredPermission: 'perm_subdomain_user_manage_students',
        },
        {
          name: 'Management',
          route: '/admin/users-and-roles/instructors',
          requiredPermission: 'perm_subdomain_user_manage_all_non_enterprise',
        },
      ],
    },
    {
      name: 'Group',
      route: '/admin/group',
      icon: GroupIcon,
      requiredPermission: 'perm_group_view',
    },
    {
      name: 'Courses',
      route: '/admin/',
      icon: CoursesIcon,
      requiredPermission: 'perm_course_view',
      subItems: [
        { name: 'All Courses', route: '/admin/courses', requiredPermission: 'perm_course_view' },
        {
          name: 'Assistiv Docs',
          route: '/admin/courses/assistiv-docs',
          requiredPermission: 'perm_course_view',
        },
        {
          name: 'Assistiv Flow',
          route: '/admin/courses/assistiv-flow',
          requiredPermission: 'perm_course_view',
        },
        {
          name: 'Assistiv Docs Edit',
          route: '/admin/courses/assistiv-docs/edit',
          requiredPermission: 'perm_course_manage',
        },
      ],
    },
    {
      name: 'Grades',
      route: '/admin/grades',
      icon: GradesIcon,
      requiredPermission: 'perm_group_view',
    },
    {
      name: 'Reports',
      route: '/admin/',
      icon: ReportsIcon,
      requiredPermission: 'perm_view_reports',
      subItems: [
        // {
        //   name: 'Group Overview',
        //   route: '/admin/reports',
        //   requiredPermission: 'perm_group_analytics',
        // },
        {
          name: 'Transcripts',
          route: '/admin/transcripts',
          requiredPermission: 'perm_view_reports',
        },
      ],
    },
    { name: 'Grader', route: '/admin/grades/grader', requiredPermission: 'perm_group_grade_edit' },
    {
      name: 'System Setup',
      route: '/admin/system-setup',
      icon: SettingsIcon,
      subItems: [
        {
          name: 'SAML 2.0 SSO',
          route: '/admin/system-setup/saml-setup',
          requiredPermission: 'perm_sso_saml',
        },
        // {
        //   name: 'Pending Admin Accounts',
        //   route: '/admin/system-setup/admin-queue',
        //   requiredPermission: 'perm_enterprise_exclusive',
        // },
        // {
        //   name: 'User Registration',
        //   route: '/admin/system-setup/user-registration',
        //   requiredPermission: 'perm_subdomain_user_manage_students',
        // },
        {
          name: 'Role Management',
          route: '/admin/system-setup/roles',
          requiredPermission: 'perm_role_management',
        },
        {
          name: 'Create Custom Role',
          route: '/admin/system-setup/roles/create-custom-role',
          requiredPermission: 'perm_role_management',
        },
        {
          name: 'Edit Default Role',
          route: '/admin/system-setup/roles/edit-default-role',
          requiredPermission: 'perm_role_management',
        },
      ],
    },
  ],
};

export const NavItemHideList: Record<string, Array<string>> = {
  student: [
    'Lesson',
    'Flash Cards',
    'Flash Cards Review',
    'Self Quiz Review',
    'Self Quiz',
    'Tutor Me',
  ],
  admin: ['Grader', 'Assistiv Docs Edit', 'Create Custom Role', 'Edit Default Role'],
};

const HiddenAccessRouteMap: Record<NavRoleKey, Array<HiddenAccessRoute>> = {
  student: [
    { route: '/student/profile' },
    { route: '/student/courses', requiredPermission: 'perm_course_view' },
    { route: '/student/playground', requiredPermission: 'perm_ai_tools' },
  ],
  admin: [],
};

const getNavRoleKey = (
  userRole: role,
  effectiveRole: EffectiveRole | null | undefined,
): NavRoleKey | null => {
  if (effectiveRole?.roleType) {
    return effectiveRole.roleType === 'student' ? 'student' : 'admin';
  }
  if (!userRole) {
    return null;
  }
  return userRole === 'student' ? 'student' : 'admin';
};

const normalizePath = (path: string): string => {
  if (!path) return '/';
  const cleaned = path.split('#')[0]?.split('?')[0]?.trim() ?? '/';
  if (cleaned.length > 1 && cleaned.endsWith('/')) {
    return cleaned.slice(0, -1);
  }
  return cleaned || '/';
};

const hasPermission = (
  requiredPermission: keyof RolePermissionFlags | undefined,
  effectiveRole: EffectiveRole | null | undefined,
): boolean => {
  if (!requiredPermission) {
    return true;
  }
  if (!effectiveRole) {
    return false;
  }
  return EffectiveRoleService.hasPermission(effectiveRole, requiredPermission);
};

const collectRoutes = (navItems: Array<NavItem>): Array<string> => {
  const routes: string[] = [];
  navItems.forEach((item) => {
    routes.push(item.route);
    item.subItems?.forEach((subItem) => {
      routes.push(subItem.route);
    });
  });
  return routes;
};

export const filterNavItemsByPermissions = (
  navItems: Array<NavItem>,
  effectiveRole: EffectiveRole | null | undefined,
): Array<NavItem> => {
  return navItems
    .map((item) => {
      if (!hasPermission(item.requiredPermission, effectiveRole)) {
        return null;
      }

      if (!item.subItems || item.subItems.length === 0) {
        return item;
      }

      const permittedSubItems = item.subItems.filter((subItem) =>
        hasPermission(subItem.requiredPermission, effectiveRole),
      );
      if (permittedSubItems.length === 0) {
        return null;
      }

      return {
        ...item,
        subItems: permittedSubItems,
      };
    })
    .filter((entry): entry is NavItem => entry !== null);
};

export const resolveFirstPermittedRoute = (
  userRole: role,
  effectiveRole: EffectiveRole | null | undefined,
): string | null => {
  const navRoleKey = getNavRoleKey(userRole, effectiveRole);
  if (!navRoleKey) {
    return null;
  }

  const permittedNavItems = filterNavItemsByPermissions(
    NavItemMap[navRoleKey] ?? [],
    effectiveRole,
  );
  const firstNavItem = permittedNavItems[0];
  if (firstNavItem) {
    const nextRoute = firstNavItem.subItems?.[0]?.route ?? firstNavItem.route;
    return normalizePath(nextRoute);
  }

  const hiddenRoutes = (HiddenAccessRouteMap[navRoleKey] ?? [])
    .filter((route) => hasPermission(route.requiredPermission, effectiveRole))
    .map((route) => normalizePath(route.route));

  return hiddenRoutes[0] ?? null;
};

export const isPathPermittedForRole = (
  pathname: string,
  userRole: role,
  effectiveRole: EffectiveRole | null | undefined,
): boolean => {
  const navRoleKey = getNavRoleKey(userRole, effectiveRole);
  if (!navRoleKey) {
    return false;
  }

  const permittedNavItems = filterNavItemsByPermissions(
    NavItemMap[navRoleKey] ?? [],
    effectiveRole,
  );
  const navRoutes = collectRoutes(permittedNavItems);
  const hiddenRoutes = (HiddenAccessRouteMap[navRoleKey] ?? [])
    .filter((route) => hasPermission(route.requiredPermission, effectiveRole))
    .map((route) => route.route);

  const permittedRoutes = new Set(
    [...navRoutes, ...hiddenRoutes].map((route) => normalizePath(route)),
  );
  return permittedRoutes.has(normalizePath(pathname));
};
